import React, { type PropsWithChildren, useMemo } from "react";
import type {
  Competitor,
  Event as EventType,
  OutcomeWithId,
} from "hooks/firestore/betting/useBetting";
import type { Title } from "hooks/graphql/useTitles";
import cx from "classnames";
import { TitleIcon } from "components/TitleIcon";
import { StreamButton } from "components/layout/StreamButton";
import { Clamp } from "components/Clamp";
import { SameGameMultiPill } from "../components/SameGameMultiPill/SameGameMultiPill";
import { Icon } from "library";
import { navigate } from "utilities/navigate";
import { Link } from "components/Link";
import { EventStatus, StatusEnum } from "components/layout/EventStatus";
import { ReactComponent as EnclosedPromoIcon } from "components/assets/icon-promo-inside.svg";
import { ReactComponent as ArrowDown } from "components/assets/flucs-down.svg";
import { ReactComponent as ArrowUp } from "components/assets/flucs-up.svg";
import { getHeadToHeadOutcomes } from "utilities/sharedBettingUtilities";
import Odds from "../Odds";
import { useDisplayAtLeast, useIsLoggedIn, useIsMobile } from "hooks";
import { isPast } from "date-fns";
import { OddsChanges } from "components/OddsChanges";
import { titleCase } from "title-case";
import { CompetitorAvatar } from "../components/CompetitorAvatar";
import { usePreferences } from "hooks/firestore/v2/user/usePreferences";

import * as styles from "./MatchCard.module.scss";

type OutcomeClickHandler = (outcome: OutcomeWithId) => void;

type MatchCardProps = {
  event: EventType;
  title: Title;
  onOutcomeClick: (event: EventType, outcome: OutcomeWithId) => void;
  oddsFormat?: string;
  selections: string[];
  disableClick?: boolean;
};

type MatchCardHeaderProps = PropsWithChildren<{
  event: EventType;
  title: Title;
}>;

type MatchCardOutcomesProps = {
  event: EventType;
  onOutcomeClick: OutcomeClickHandler;
  oddsFormat?: string;
  selections: string[];
};

type MatchCardOutcomeProps = {
  outcome: OutcomeWithId;
  onClick: OutcomeClickHandler;
  competitor?: Competitor;
  className?: string;
  isSelected?: boolean;
  disabled?: boolean;
  oddsFormat?: string;
  isHighlighted?: boolean;
};

const MatchCardOutcome = ({
  outcome,
  competitor,
  className,
  onClick,
  isSelected,
  disabled,
  oddsFormat,
}: MatchCardOutcomeProps) => {
  const { preferences: userPreferences } = usePreferences();

  const isDisabled =
    disabled || outcome.odds === 0 || outcome.odds === 1 || !outcome.active;

  const isHighlighted = outcome.highlighted;
  const isPickems = oddsFormat === "points";

  return (
    <OddsChanges
      odds={Number(outcome.odds)}
      className={cx(styles.outcome, className, {
        [styles.selected]: isSelected,
        [styles.highlighted]: isHighlighted,
        [styles.disabled]: isDisabled,
        [styles.won]: outcome.result === "WIN",
        [styles.lostOrVoid]:
          outcome.result === "LOSE" || outcome.result === "VOID",
      })}
      onClick={(e) => {
        e.stopPropagation();
        onClick(outcome);
      }}
      animationLength={10000}
    >
      {competitor && <CompetitorAvatar competitor={competitor} />}

      <div className={styles.nameAndOdds}>
        <span className={cx(styles.name)}>{outcome.name}</span>
        <div className={styles.oddsWrapper}>
          <Odds
            base={Number(outcome.odds)}
            zero={`-`}
            format={oddsFormat ?? userPreferences?.oddsFormat.toLowerCase()}
            className={styles.odds}
          />

          <div
            className={cx(styles.oddsChangeArrowUp, {
              [styles.arrowUpForPickems]: isPickems,
            })}
          >
            <ArrowUp />
          </div>

          <div
            className={cx(styles.oddsChangeArrowDown, {
              [styles.arrowDownForPickems]: isPickems,
            })}
          >
            <ArrowDown />
          </div>
        </div>
      </div>
    </OddsChanges>
  );
};

const VersusDivider = () => {
  return (
    <div className={styles.versusDivider}>
      <span>vs</span>
    </div>
  );
};

const shouldOutcomesBeDisabled = (status: string) => {
  return ["SUSPENDED", "SETTLED", "DEACTIVATED", "UNAVAILABLE"].includes(
    status,
  );
};

const MatchCardOutcomes = ({
  event,
  onOutcomeClick,
  oddsFormat,
  selections,
}: MatchCardOutcomesProps) => {
  const {
    homeOutcome,
    awayOutcome,
    drawOutcome,
    homeCompetitor,
    awayCompetitor,
  } = useMemo(() => {
    const headToHeadOutcomes = getHeadToHeadOutcomes(event);
    const homeCompetitor =
      event.competitors?.[headToHeadOutcomes.homeOutcome?.competitorId ?? ""];
    const awayCompetitor =
      event.competitors?.[headToHeadOutcomes.awayOutcome?.competitorId ?? ""];

    return {
      ...getHeadToHeadOutcomes(event),
      homeCompetitor,
      awayCompetitor,
    };
  }, [event]);
  const isDisabled = shouldOutcomesBeDisabled(event.mainMarket?.status ?? "");

  return (
    <div className={cx(styles.outcomes, { [styles.hasDraw]: !!drawOutcome })}>
      {homeOutcome && (
        <MatchCardOutcome
          outcome={homeOutcome}
          competitor={homeCompetitor}
          onClick={onOutcomeClick}
          isSelected={selections.includes(homeOutcome.id)}
          disabled={isDisabled}
          oddsFormat={oddsFormat}
        />
      )}
      {drawOutcome ? (
        <MatchCardOutcome
          outcome={drawOutcome}
          className={styles.draw}
          onClick={onOutcomeClick}
          isSelected={selections.includes(drawOutcome.id)}
          disabled={isDisabled}
          oddsFormat={oddsFormat}
        />
      ) : (
        <VersusDivider />
      )}
      {awayOutcome && (
        <MatchCardOutcome
          outcome={awayOutcome}
          competitor={awayCompetitor}
          onClick={onOutcomeClick}
          isSelected={selections.includes(awayOutcome.id)}
          disabled={isDisabled}
          oddsFormat={oddsFormat}
        />
      )}
    </div>
  );
};

const MatchCardCluster = ({
  event,
  disabled,
  suspended,
}: { event: EventType; disabled: boolean; suspended: boolean }) => {
  return (
    <div className={styles.cluster}>
      <div className={styles.clusterRow}>
        {["ACTIVE", "LIVE"].includes(event.status) && (
          <StreamButton
            title={event.eventName}
            sport={event.sport}
            url={event.streamUri ?? ""}
            startTime={event.nextBetStop || event.scheduledStartTime}
            status={event.status}
            eventId={event.eventId}
            isPlaceholder={event?.attributes?.streamExpected}
            hideIfNotThirtyMinutesBeforeStart
          />
        )}

        {suspended ? (
          <Clamp variant="disabled">
            <Icon type="lock" className={styles.lockIcon} />
          </Clamp>
        ) : (
          <EventStatus
            status={event.status}
            scheduledStartTime={event.nextBetStop || event.scheduledStartTime}
            clampClassName={styles.clampRight}
          />
        )}
      </div>
      <div className={cx(styles.clusterRow, styles.sgmAndMarkets)}>
        {event.isSGMReady && !disabled && !suspended && (
          <Link
            to={`${event.hub === "sports" ? "/sports" : ""}/betting/match/${
              event.eventId
            }/sgm`}
            onClick={(event) => event.stopPropagation()}
          >
            <SameGameMultiPill className={styles.sgmButton} />
          </Link>
        )}
        {event.activeMarketCount > 1 && (
          <Link
            to={`${event.hub === "sports" ? "/sports" : ""}/betting/match/${
              event.eventId
            }`}
          >
            <Clamp className={cx(styles.clampRight, styles.marketsClamp)}>
              {event.activeMarketCount}
              <Icon type="arrow-right" className={styles.arrowIcon} inline />
            </Clamp>
          </Link>
        )}
      </div>
    </div>
  );
};

const MatchCardHeader = ({ children, event, title }: MatchCardHeaderProps) => {
  const isLoggedIn = useIsLoggedIn();
  const isAtLeastTablet = useDisplayAtLeast("tablet");

  /** helps display the match mode alongside sport or round name */
  const renderStringWithMatchMode = (str: string, matchMode: string) => {
    // if we are on tablet or above viewport the match mode will be displayed on
    // a new line below the sport/round already
    if (!matchMode || typeof matchMode !== "string" || isAtLeastTablet) {
      return str;
    }

    return `${str} (${matchMode.toUpperCase()})`;
  };

  const eventTitle = event.attributes.tournamentName ?? event.eventName;

  return (
    <div className={styles.header}>
      <div className={styles.icon}>
        <TitleIcon title={title} svg className={styles.titleIcon} />
        {isLoggedIn &&
          (event.hasPromotionalMarkets ||
            event.promotionCampaigns?.length > 0) && (
            <div className={styles.promoIcon}>
              <EnclosedPromoIcon />
            </div>
          )}
      </div>

      <div className={styles.titleWrapper}>
        <h3
          className={cx(styles.title, {
            [styles.small]: eventTitle.length > 30,
          })}
        >
          {eventTitle}
        </h3>
        <span className={styles.subtitle}>
          {renderStringWithMatchMode(
            event.attributes.roundName &&
              typeof event.attributes.roundName === "string"
              ? titleCase(event.attributes.roundName.replaceAll("_", " "))
              : title.name,
            event.attributes.matchMode,
          )}
        </span>
        {isAtLeastTablet && event.attributes.matchMode && (
          <span className={styles.matchMode}>{event.attributes.matchMode}</span>
        )}
      </div>

      {children}
    </div>
  );
};

/** Our match card, visible on the sports next to go page */
const MatchCard = ({
  event,
  title,
  onOutcomeClick,
  oddsFormat,
  selections,
  disableClick = false,
}: MatchCardProps) => {
  const isMobile = useIsMobile();

  if (Object.keys(event.mainMarket?.outcomes ?? {}).length === 0) {
    return null;
  }

  const isDisabled = event.status !== "ACTIVE";
  const hasStarted = isPast(event.scheduledStartTime);
  const isSuspended = event.status === StatusEnum.SUSPENDED && !hasStarted;

  return (
    <div
      className={cx(styles.matchCard, {
        [styles.disabled]: isDisabled,
        [styles.normalCursor]: disableClick,
      })}
      onClick={async () => {
        if (isDisabled || disableClick) return;
        await navigate(
          `${event.hub === "sports" ? "/sports" : ""}/betting/match/${
            event.eventId
          }`,
        );
      }}
    >
      <MatchCardHeader event={event} title={title}>
        {isMobile && (
          <MatchCardCluster
            event={event}
            disabled={isDisabled}
            suspended={isSuspended}
          />
        )}
      </MatchCardHeader>

      <MatchCardOutcomes
        oddsFormat={oddsFormat}
        event={event}
        onOutcomeClick={(outcome) => onOutcomeClick(event, outcome)}
        selections={selections}
      />

      {!isMobile && (
        <MatchCardCluster
          event={event}
          disabled={isDisabled}
          suspended={isSuspended}
        />
      )}
    </div>
  );
};

MatchCard.displayName = "MatchCard";

export { MatchCard };
